<template>
  <div>
    <nav :aria-label="$t.primaryNavigationLabel" class="relative overflow-hidden">
      <transition
        enter-active-class="slide-enter-active"
        :enter-from-class="`slide-enter-from ${isGoingBack ? 'slide-right' : 'slide-left'}`"
        leave-active-class="slide-leave-active absolute top-0 w-full"
        :leave-to-class="`slide-leave-to ${isGoingBack ? 'slide-left' : 'slide-right'}`"
        style="transition-duration: 0.5s;"
        @after-enter="handleAfterEnter"
      >
        <!-- L1 Menu -->
        <div v-if="!activeL1?.id">
          <!-- L1 Items -->
          <ul>
            <!-- XPLR Pass Section -->
            <loyalty-nav-item v-if="$feature.enableLoyaltyFeatures && !user.isWranxOrIpaProfile" class="m-4" />
            <li
              v-for="menuItem in items.children"
              :key="menuItem.id"
              :ref="(el) => l1Refs[menuItem.id] = el as HTMLElement"
            >
              <cms-section :section="{ name: menuItem.id, items: [menuItem] }" />
            </li>
          </ul>
        </div>

        <div v-else>
          <!-- Back Button -->
          <base-button
            ref="backButtonRef"
            :aria-label="activeL2?.id ? `${$t.backTo} ${activeL1!.title}` : $t.backToMainMenu"
            class="h-16 w-full flex items-center b-b b-grey-70 px-3 py-4 body-1 fw-bold"
            @click="handleBackClick"
          >
            <vf-icon dir="left" name="chevron" size="lg" />
            <span class="duration" :class="{ 'truncate c-grey-40': activeL2?.id }">
              <span v-if="$feature.showTitleHtml" v-html="activeL1.titleHtml" />
              <template v-else>
                {{ activeL1.title }}
              </template>
            </span>
            <transition name="fade">
              <span v-if="activeL2?.id" class="ml-2 flex items-center gap-2 ws-nowrap">
                <span class="c-grey-40">/</span>
                <span v-if="$feature.showTitleHtml" v-html="activeL2.titleHtml" />
                <template v-else>
                  {{ activeL2.title }}
                </template>
              </span>
            </transition>
          </base-button>
          <transition
            enter-active-class="slide-enter-active"
            :enter-from-class="`slide-enter-from ${isGoingBack ? 'slide-right' : 'slide-left'}`"
            leave-active-class="slide-leave-active absolute w-full"
            :leave-to-class="`slide-leave-to ${isGoingBack ? 'slide-left' : 'slide-right'}`"
            style="top: 4rem; transition-duration: 0.5s;"
            @after-enter="handleAfterEnter"
          >
            <!-- L2 Menu -->
            <div v-if="!activeL2?.id">
              <cms-section
                v-if="activeL1.visualSelectors"
                :section="{ items: activeL1.visualSelectors, name: 'visual-selectors' }"
              />
              <ul class="pt-4">
                <!-- L2 Shop All -->
                <li v-if="!activeL1?.hideShopAll?.sm && activeL1?.target?.url">
                  <base-link class="block px-5 py-4 title-3" :to="activeL1.target.url" @click="emit('close')">
                    <span
                      v-if="$feature.showTitleHtml"
                      v-html="activeL1.overrideShopAll || `${$t.megaMenuShopAll} ${activeL1.titleHtml}`"
                    />
                    <template v-else>
                      {{ activeL1?.overrideShopAll || `${$t.megaMenuShopAll} ${activeL1?.title}` }}
                    </template>
                  </base-link>
                </li>
                <!-- L2 Items: regular + featured ordered by featuredColumnPosition -->
                <template v-for="(section, i) in l2Sections" :key="section.type">
                  <div v-if="hasFeaturedColumn && i > 0" class="m-4 b-t b-grey-70" />
                  <li
                    v-for="menuItem in section.items"
                    :key="menuItem.id"
                    :ref="(el) => l2Refs[menuItem.id] = el as HTMLElement"
                    class="body-1"
                    :class="{ 'fw-bold': section.type === 'featured' }"
                  >
                    <cms-section :section="{ name: menuItem.id, items: [menuItem] }" />
                  </li>
                </template>
              </ul>
            </div>

            <!-- L3 Menu -->
            <ul v-else class="pt-4">
              <li v-if="!activeL2?.hideShopAll?.sm && activeL2?.target?.url">
                <!-- L3 Shop All -->
                <base-link class="block px-5 py-4 title-3" :to="activeL2.target.url" @click="emit('close')">
                  <span
                    v-if="$feature.showTitleHtml"
                    v-html="activeL2.overrideShopAll || `${$t.megaMenuShopAll} ${activeL2.titleHtml}`"
                  />
                  <template v-else>
                    {{ activeL2?.overrideShopAll || `${$t.megaMenuShopAll} ${activeL2?.title}` }}
                  </template>
                </base-link>
              </li>
              <!-- L3 Items -->
              <li
                v-for="menuItem in activeL2?.children?.filter(({ hideOnWeb }) => !hideOnWeb)"
                :key="menuItem.id"
              >
                <cms-section :section="{ name: menuItem.id, items: [menuItem] }" />
              </li>
            </ul>
          </transition>
        </div>
      </transition>
    </nav>

    <transition name="fade" style="transition-duration: 0.5s">
      <div v-if="!activeL1?.id">
        <div v-if="links || items.utilityLinks?.length" class="mx-4 mt-4 f-col items-start gap-4 b-t b-grey-70 pb-4 pt-8 overline-1">
          <template v-for="{ id, title, target } in items.utilityLinks" :key="id">
            <component
              :is="getActionLinkComponent(target)"
              v-if="canRenderActionLink(target)"
              class="flex items-center gap-2"
              :class="{ 'cursor-pointer bg-transparent p-0 text-left': isActionLinkOnly(target) }"
              v-bind="getActionLinkAttrs(target)"
              @click="handleUtilityLinkClick(target, title)"
            >
              <vf-icon v-if="target.icon" :dir="target.icon.dir" :name="target.icon.icon" size="md" />
              {{ title }}
            </component>
          </template>
          <vf-utility-navigation
            v-if="links"
            class="f-col items-start gap-4 o:overline-1"
            :links
            @click="emit('close')"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts" setup>
import type { MegaMenuContent, MenuItem } from '#types/components/cms/mega-menu'
import type { NavigationLink } from '#types/navigation'
import type { Target } from '#types/target'
import { BaseButton } from '#components'
import { MegaMenuL1ContextKey, MegaMenuL2ContextKey, MegaMenuMobileContextKey } from '#content/contexts/megamenu'

const { items } = defineProps<{
  items: MegaMenuContent
  links?: NavigationLink[]
}>()

const emit = defineEmits<{
  close: []
}>()

const auth = useAuthStore()
const { $feature, $t } = useNuxtApp()
const {
  canRenderActionLink,
  getActionLinkAttrs,
  getActionLinkComponent,
  getActionLinkHandler,
  isActionLinkOnly,
} = useCmsActionLink('header:mega-menu')
const loyalty = useLoyaltyStore()
const backButtonRef = useTemplateRef<InstanceType<typeof BaseButton>>('backButtonRef')
const user = useUserStore()

const handleUtilityLinkClick = (target: Target, title: string) => {
  if (isActionLinkOnly(target)) getActionLinkHandler(target, title)?.()
  emit('close')
}

const activeL1 = ref<MenuItem>({} as MenuItem)
const activeL2 = ref<MenuItem>()
const l1Refs = ref<Record<string, HTMLElement | null>>({})
const l2Refs = ref<Record<string, HTMLElement | null>>({})
const pendingFocusId = ref<string | null>(null)
const isGoingBack = ref(false)

// Reset direction flag when a sub-component drives forward navigation
watch(
  [() => !!activeL1.value?.id, () => !!activeL2.value?.id],
  ([hasL1, hasL2], [hadL1, hadL2]) => {
    if ((hasL1 && !hadL1) || (hasL2 && !hadL2)) isGoingBack.value = false
  },
  { flush: 'sync' },
)

const filteredColumns = computed(() => {
  const activeL1Item = activeL1.value as MenuItem
  if (activeL1Item.l2ReorderSmall?.length) {
    const allChildren = activeL1Item.columns?.flatMap((col) =>
      col.children?.filter(({ hideOnWeb }) => !hideOnWeb) ?? []
    ) ?? []
    const orderedIds = activeL1Item.l2ReorderSmall.map(({ id }) => id)
    const ordered = orderedIds.map((id) => allChildren.find((child) => child.id === id)).filter(Boolean) as MenuItem[]
    const rest = allChildren.filter((child) => !orderedIds.includes(child.id))
    return [{ children: [...ordered, ...rest] }]
  }
  return activeL1Item.columns?.map((column) => ({
    ...column,
    children: column.children?.filter(({ hideOnWeb }) => !hideOnWeb) ?? [],
  })) ?? []
})

const filteredFeaturedColumn = computed(() =>
  (activeL1.value as MenuItem).featuredColumn?.filter(({ hideOnWeb }) => !hideOnWeb) ?? []
)

const isFeaturedTop = computed(() =>
  (activeL1.value as MenuItem).featuredColumnPosition === 'top'
)

const hasFeaturedColumn = computed(() => !!filteredFeaturedColumn.value.length)

const l2Sections = computed(() => {
  const regular = { type: 'regular' as const, items: filteredColumns.value.flatMap(({ children = [] }) => children) }
  const featured = { type: 'featured' as const, items: filteredFeaturedColumn.value }
  return isFeaturedTop.value ? [featured, regular] : [regular, featured]
})

const focusBackButton = () => backButtonRef.value?.$el?.focus()
const focusL1Button = (id: string) => l1Refs.value[id]?.querySelector<HTMLButtonElement>('button')?.focus()
const focusL2Button = (id: string) => l2Refs.value[id]?.querySelector<HTMLButtonElement>('button')?.focus()

const handleAfterEnter = () => {
  isGoingBack.value = false
  if (pendingFocusId.value) {
    const id = pendingFocusId.value
    pendingFocusId.value = null
    activeL1.value?.id ? focusL2Button(id) : focusL1Button(id)
  }
  else if (activeL1.value?.id) {
    focusBackButton()
  }
}

const handleBackClick = () => {
  isGoingBack.value = true
  if (activeL2.value?.id) {
    pendingFocusId.value = activeL2.value.id
    activeL2.value = undefined
  }
  else {
    pendingFocusId.value = activeL1.value?.id
    activeL1.value = {} as MenuItem
  }
}

provide(MegaMenuMobileContextKey, true)
provide(MegaMenuL1ContextKey, {
  activeL1,
  activeL2,
  l1: () => formatMegaMenuLevelLabel(activeL1.value?.delocalizedTracking || activeL1.value?.name, 1)
})
provide(MegaMenuL2ContextKey, {
  featured: false,
  title: () => formatMegaMenuLevelLabel(activeL2.value?.delocalizedTracking || activeL2.value?.name, 2)
})

// Fetch vouchers when authenticated
watch(
  () => auth.loggedIn,
  (loggedIn) => {
    if (loggedIn && !loyalty.vouchers) loyalty.getVouchers()
  },
  { immediate: true }
)
</script>
